import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { BrowserRouter } from 'react-router-dom'
import { FloatingToastStack } from './components/notifications/FloatingToastStack'
import './i18n' // Initialize i18next (side-effect) before App's module graph evaluates.
import App from './App.tsx'
import { callMachine } from './telephony/callMachine'
import { registerServiceWorker } from './pwa/registerServiceWorker'
import { retryUnlessRateLimited } from './utils/queryRetry'
import { installFrontendErrorReporting } from './services/frontendErrorReporter'
import './index.css'

// ⚠️ UNIQUE QueryClient de l'application (et unique QueryClientProvider).
// Il y en avait deux imbriqués (ici + App.tsx) : c'est celui d'App.tsx qui gagnait
// (provider le plus proche), donc sa configuration — plus riche — est celle
// conservée ici. Ne pas réintroduire de second provider : deux QueryClient = deux
// caches, et les hooks montés de part et d'autre ne partagent plus rien.
/**
 * LOT-BLINDAGE (B2) — CHUNKS PÉRIMÉS APRÈS DÉPLOIEMENT.
 *
 * Le statique Railway répond **200 `text/html`** (l'index) pour un chunk haché
 * ABSENT de `/assets/` : après un déploiement, un onglet resté ouvert qui
 * demande un ancien chunk reçoit du HTML → `import()` rejette → `React.lazy`
 * jette EN RENDU et met l'échec en cache (« Réessayer » ne répare jamais).
 * Vite signale ces échecs de préchargement via l'événement `vite:preloadError` :
 * on recharge la page — le nouvel index référence les nouveaux chunks.
 *
 * GARDE « APPEL EN COURS » (correctif de revue architecte) : le boundary
 * interne (B1) protège un appel d'un CRASH de rendu — il ne protège de RIEN
 * face à un `location.reload()`, qui détruit le Device Twilio et raccroche la
 * ligne. Or toutes les pages sont `React.lazy` : un agent EN LIGNE qui navigue
 * vers une page dont le chunk est périmé déclenche exactement cet événement.
 * Recharger ici tuerait l'appel — le mal que ce lot combat. Tant que la
 * machine d'appel n'est pas au repos (`idle`), on NE recharge PAS et on laisse
 * l'erreur suivre son cours : B1 affiche l'écran de repli, l'appel survit, et
 * l'agent rechargera lui-même une fois raccroché. Seul `idle` autorise le
 * rechargement : `wrapup` = qualification en cours de saisie (à ne pas
 * perdre), `preview` = enchaînement de campagne armé, les autres = ligne
 * vivante.
 *
 * GARDE ANTI-BOUCLE : un seul rechargement automatique par minute (marqueur
 * `sessionStorage`). Si l'erreur rejoue dans la minute (vrai problème réseau,
 * asset réellement cassé), on laisse l'erreur suivre son cours : le boundary
 * l'affichera au lieu de boucler sur `location.reload()`.
 *
 * Exportée pour la spec (`main.staleChunkReload.spec.ts`) — `main.tsx` a des
 * effets de bord au chargement, la fonction se teste donc via des doublons
 * `win`/`storage`/`isSafeToReload` injectés.
 */
export const STALE_CHUNK_RELOAD_KEY = 'stale-chunk-reloaded-at'
export const STALE_CHUNK_RELOAD_MIN_INTERVAL_MS = 60_000

/**
 * Prédicat par défaut : le poste est au repos. `callMachine` est un store pur
 * (aucun import de `device.ts`, aucun accès DOM au chargement) et il est déjà
 * dans le graphe initial via `App.tsx` → `TelephonyProvider` : l'importer ici
 * n'ajoute ni effet de bord ni octet au bundle.
 */
export function isCallMachineIdle(): boolean {
  return callMachine.getSnapshot().phase === 'idle'
}

export function installStaleChunkReload(
  win: Pick<Window, 'addEventListener'> & { location: { reload(): void } } = window,
  storage: Pick<Storage, 'getItem' | 'setItem'> = sessionStorage,
  isSafeToReload: () => boolean = isCallMachineIdle,
): void {
  win.addEventListener('vite:preloadError', (event) => {
    if (!isSafeToReload()) {
      // Ligne engagée (ou saisie en cours) : ni preventDefault, ni reload, ni
      // marqueur — l'erreur suit son cours et le boundary (B1) l'affiche. Le
      // rechargement reviendra de lui-même au prochain chunk périmé rencontré
      // une fois le poste au repos.
      console.warn(
        '[blindage] Chunk périmé détecté mais rechargement différé : appel en cours',
        { phase: callMachine.getSnapshot().phase },
      )
      return
    }
    let lastReloadAt = 0
    try {
      lastReloadAt = Number(storage.getItem(STALE_CHUNK_RELOAD_KEY) ?? '0') || 0
    } catch {
      // sessionStorage inaccessible : on recharge quand même (au pire, la
      // boucle est bornée par le serveur qui finit par servir le bon index).
    }
    if (Date.now() - lastReloadAt < STALE_CHUNK_RELOAD_MIN_INTERVAL_MS) {
      // Déjà rechargé il y a moins d'une minute : ne pas boucler, laisser
      // l'erreur suivre son cours (React.lazy jettera, le boundary affichera).
      return
    }
    try {
      storage.setItem(STALE_CHUNK_RELOAD_KEY, String(Date.now()))
    } catch {
      // Ignoré — voir ci-dessus.
    }
    event.preventDefault()
    win.location.reload()
  })
}

installStaleChunkReload()

// LOT #146 — rejets de promesse non gérés → serveur (les erreurs de rendu passent par l'ErrorBoundary).
installFrontendErrorReporting()

/**
 * LOT-SW-REPOS — B2 ci-dessus attrape la CHUTE (chunk déjà périmé) ; ceci
 * retire la TRAPPE : le nouveau service worker attend (`registerType:
 * 'prompt'`, `skipWaiting: false`) et n'est appliqué — silencieusement — que
 * quand le poste est au repos (même prédicat `isCallMachineIdle`, via
 * `callMachine`). Un onglet en appel garde donc ses chunks. Voir
 * `pwa/swUpdate.ts` et docs/architecture/PWA-MISE-A-JOUR.md.
 */
registerServiceWorker()

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // Données fraîches 5 minutes
      gcTime: 30 * 60 * 1000, // Purge du cache après 30 minutes (ex-cacheTime en v5)
      refetchOnWindowFocus: false, // Pas de refetch au retour de focus
      // Une seule nouvelle tentative — jamais sur un 429 (#194, D8) : rejouer
      // tout de suite une requête refusée par le limiteur prolonge le blocage.
      retry: retryUnlessRateLimited,
    },
  },
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
        {/* LOT-H9 — la pile de notifications vit désormais DANS le flux, sous
            l'en-tête (`NotificationRegion`, montée par l'`AppShell`). Ce
            composant-ci ne rend quelque chose que sur les écrans SANS châssis
            (`/login`, `/register`), où il n'y a aucune zone d'action à
            recouvrir. Il reste ici, au-dessus du routeur, parce que ces
            écrans-là en ont besoin. */}
        <FloatingToastStack />
        <ReactQueryDevtools initialIsOpen={false} />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
)
